import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import NotFoundPage from "@dashboard/components/NotFoundPage";
import {
  useWsmFitmentSchemaDeleteMutation,
  useWsmFitmentSchemaUpdateMutation,
  WsmErrorFragment,
  WsmFitmentAxisFragment,
  WsmFitmentSchemaFragment,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { Skeleton, Text } from "@saleor/macaw-ui-next";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";

import { removeFromWsmFitmentList } from "../cache";
import { WsmFitmentLoadError } from "../components/WsmFitmentLoadError";
import WsmFitmentSchemaDetailsPage from "../components/WsmFitmentSchemaDetailsPage";
import { getWsmFitmentErrorMessage } from "../errors";
import { useWsmFitmentAttempt } from "../hooks/useWsmFitmentAttempt";
import { useWsmFitmentAxes, useWsmFitmentSchemas } from "../hooks/useWsmFitmentVocabulary";
import { schemaMessages, sectionMessages } from "../messages";
import { wsmFitmentSchemaListPath } from "../urls";
import { getSchemaInput, getWriteState, takeSchemaSnapshot, toWriteResult } from "../utils";

interface SchemaEditorProps {
  schema: WsmFitmentSchemaFragment;
  axes: WsmFitmentAxisFragment[];
  axesLoading: boolean;
  onDelete: () => void;
}

/**
 * The form, mounted once the schema is loaded so it can take its snapshot on
 * mount: the form's data and the axes a save compares against are that one
 * copy, and a successful save replaces both from the schema the server returned.
 */
const WsmFitmentSchemaEditor = ({ schema, axes, axesLoading, onDelete }: SchemaEditorProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [snapshot, setSnapshot] = useState(() => takeSchemaSnapshot(schema));
  // Payload errors render on the form; a toast would say them twice.
  const [updateSchema, updateSchemaOpts] = useWsmFitmentSchemaUpdateMutation({
    disableNestedErrorHandling: true,
    onCompleted: result => {
      const saved = result.wsmFitmentSchemaUpdate?.schema;

      if (saved && !result.wsmFitmentSchemaUpdate?.errors.length) {
        setSnapshot(previous => takeSchemaSnapshot(saved, previous));
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const payload = updateSchemaOpts.data?.wsmFitmentSchemaUpdate;

  return (
    <WsmFitmentSchemaDetailsPage
      key={`${schema.id}:${snapshot.revision}`}
      title={schema.name}
      schemaId={schema.id}
      axes={axes}
      axesLoading={axesLoading}
      initialData={snapshot.data}
      errors={payload?.errors ?? []}
      disabled={updateSchemaOpts.loading}
      saveButtonBarState={getWriteState(updateSchemaOpts, payload)}
      onSubmit={formData =>
        updateSchema({
          variables: {
            id: schema.id,
            input: getSchemaInput(formData, snapshot.data.axisIds),
          },
        })
      }
      onDelete={onDelete}
    />
  );
};

export const WsmFitmentSchemaDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  // Shown on the delete dialog: this call's result only, cleared on open.
  const [deleteErrors, setDeleteErrors] = useState<WsmErrorFragment[]>([]);
  const deleteAttempt = useWsmFitmentAttempt();
  const { schemas, loading, error: listError, retry } = useWsmFitmentSchemas();
  const { axes, loading: axesLoading } = useWsmFitmentAxes();
  const [deleteSchema] = useWsmFitmentSchemaDeleteMutation({
    disableNestedErrorHandling: true,
    // The row leaves the cached list here, so the list is right the moment it
    // mounts; the awaited refetch then lands while this screen still holds the
    // query, instead of being cancelled by the navigation.
    update: (cache, { data }) => {
      if (data?.wsmFitmentSchemaDelete && !data.wsmFitmentSchemaDelete.errors.length) {
        removeFromWsmFitmentList(cache, "wsmFitmentSchemas", {
          __typename: "WsmFitmentSchema",
          id,
        });
      }
    },
    refetchQueries: ["WsmFitmentSchemas"],
    awaitRefetchQueries: true,
    onCompleted: result => {
      if (result.wsmFitmentSchemaDelete && !result.wsmFitmentSchemaDelete.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmFitmentSchemaListPath);
      }
    },
  });
  const found = schemas.find(candidate => candidate.id === id);
  // This screen's own delete takes the row out of the cache before it navigates
  // away; until then it keeps showing the last copy rather than "Not found".
  const lastSeen = useRef(found);

  if (found) {
    lastSeen.current = found;
  }

  const deleting = deleteAttempt.state === "loading" || deleteAttempt.state === "success";
  const schema = found ?? (deleting ? lastSeen.current : undefined);

  // A list that failed to load says so, rather than calling the row missing.
  if ((loading || listError) && !schema) {
    return (
      <DetailPageLayout>
        <TopNav
          href={wsmFitmentSchemaListPath}
          title={intl.formatMessage(sectionMessages.schemas)}
        />
        <DetailPageLayout.Content>
          {listError ? <WsmFitmentLoadError onRetry={retry} /> : <Skeleton />}
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  if (!schema) {
    return <NotFoundPage backHref={wsmFitmentSchemaListPath} />;
  }

  return (
    <>
      <WsmFitmentSchemaEditor
        key={schema.id}
        schema={schema}
        axes={axes}
        axesLoading={axesLoading}
        onDelete={() => {
          setDeleteErrors([]);
          deleteAttempt.reset();
          setDeleteOpen(true);
        }}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(schemaMessages.deleteTitle)}
        confirmButtonState={deleteAttempt.state}
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          const result = await deleteAttempt.run(async () =>
            toWriteResult(
              (await deleteSchema({ variables: { id: schema.id } })).data?.wsmFitmentSchemaDelete,
            ),
          );

          setDeleteErrors(result.errors);
        }}
      >
        <Text display="block">
          {intl.formatMessage(schemaMessages.deleteContent, { name: schema.name })}
        </Text>
        {deleteErrors[0] && (
          <Text
            display="block"
            color="critical1"
            marginTop={2}
            data-test-id="wsm-fitment-schema-delete-error"
          >
            {getWsmFitmentErrorMessage(deleteErrors[0], intl)}
          </Text>
        )}
      </ActionDialog>
    </>
  );
};

export default WsmFitmentSchemaDetailsView;
